import { useEffect, useState } from 'react';
import { ShieldCheck, Upload, Eye, EyeOff } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type OfficeCertificateView,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, TextInput } from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';

/**
 * J04 v1 — Tela de gerenciamento de certificados digitais A1 do escritorio.
 *
 * Fluxo:
 *  - Lista mostra titulares, validade e status. Badge amarela se faltam
 *    menos de 30 dias para expirar; vermelha se ja expirou.
 *  - Botao "+ Novo certificado" abre modal com drag-drop do .pfx + senha.
 *  - O upload converte o arquivo para base64 e envia JSON. Conteudo cifrado
 *    na API; UI nunca lida com plaintext apos o upload.
 *  - Botao "Remover" faz soft delete (status=removed).
 */

interface UploadFormState {
  file: File | null;
  password: string;
  showPassword: boolean;
}

const EMPTY: UploadFormState = {
  file: null,
  password: '',
  showPassword: false,
};

export function CertificatesPage() {
  const [items, setItems] = useState<OfficeCertificateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<UploadFormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    void refetch();
  }, []);

  async function refetch(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const rows = await apiClient.officeCertificatesList();
      setItems(rows);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setLoading(false);
    }
  }

  function openNew(): void {
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  }

  function handleFile(file: File | null): void {
    if (!file) {
      setForm((s) => ({ ...s, file: null }));
      return;
    }
    if (!/\.(pfx|p12)$/i.test(file.name)) {
      setFormError('Arquivo deve ter extensão .pfx ou .p12');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFormError('Arquivo excede 2 MB');
      return;
    }
    setFormError(null);
    setForm((s) => ({ ...s, file }));
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFile(file);
  }

  async function submit(): Promise<void> {
    if (!form.file) {
      setFormError('Selecione o arquivo .pfx');
      return;
    }
    if (!form.password) {
      setFormError('Informe a senha do certificado');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const pfxBase64 = await fileToBase64(form.file);
      await apiClient.officeCertificatesUpload({
        filename: form.file.name,
        pfxBase64,
        password: form.password,
      });
      setModalOpen(false);
      await refetch();
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: OfficeCertificateView): Promise<void> {
    const label = item.holderName || item.subjectCn;
    if (!window.confirm(`Remover o certificado de "${label}"?`)) return;
    try {
      await apiClient.officeCertificatesDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--fg)]">
            Certificado digital
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-[var(--fg-muted)]">
            Cadastre o certificado A1 (.pfx/.p12) do escritório. Ele será
            usado para assinatura de petições e autenticação em tribunais.
            O conteúdo fica armazenado de forma cifrada — apenas você pode
            usá-lo.
          </p>
        </div>
        <Button onClick={openNew}>
          <Upload className="mr-1.5 h-4 w-4" />
          Novo certificado
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <ShieldCheck className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum certificado cadastrado. Clique em{' '}
              <strong>Novo certificado</strong> para enviar o .pfx do
              escritório.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Titular</th>
                <th className="px-4 py-2 font-medium">Documento</th>
                <th className="px-4 py-2 font-medium">Emissor</th>
                <th className="px-4 py-2 font-medium">Validade</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <div className="font-medium text-[var(--fg)]">
                      {item.holderName || item.subjectCn}
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--fg-muted)]">
                    {formatDocument(item.holderDocument)}
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {item.issuerCn}
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {formatDate(item.notAfter)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge item={item} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Novo certificado digital"
        description="Envie o arquivo .pfx ou .p12 do escritório e informe a senha."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Enviando…' : 'Enviar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}

          <Field label="Arquivo do certificado" required>
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-sm transition ${
                dragActive
                  ? 'border-[var(--accent)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]'
                  : 'border-[var(--border)] hover:border-[var(--fg-muted)]'
              }`}
            >
              <input
                type="file"
                accept=".pfx,.p12"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              <Upload className="h-6 w-6 text-[var(--fg-muted)]" />
              {form.file ? (
                <span className="font-medium text-[var(--fg)]">
                  {form.file.name} ({(form.file.size / 1024).toFixed(0)} KB)
                </span>
              ) : (
                <span className="text-[var(--fg-muted)]">
                  Arraste o arquivo .pfx aqui ou clique para selecionar
                </span>
              )}
            </label>
          </Field>

          <Field label="Senha do certificado" required>
            <div className="relative">
              <TextInput
                type={form.showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) =>
                  setForm((s) => ({ ...s, password: e.target.value }))
                }
                placeholder="Senha que protege o .pfx"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() =>
                  setForm((s) => ({ ...s, showPassword: !s.showPassword }))
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                aria-label={form.showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {form.showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </Field>
        </div>
      </ConfigModal>
    </div>
  );
}

function StatusBadge({ item }: { item: OfficeCertificateView }) {
  if (item.isExpired || item.status === 'expired') {
    return (
      <span className="inline-flex items-center rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-500">
        Expirado
      </span>
    );
  }
  if (item.status === 'revoked') {
    return (
      <span className="inline-flex items-center rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-500">
        Revogado
      </span>
    );
  }
  if (item.daysUntilExpiry <= 30) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-500">
        Expira em {item.daysUntilExpiry}d
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500">
      Ativo
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function formatDocument(doc: string): string {
  if (!doc) return '—';
  if (doc.length === 11) {
    return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (doc.length === 14) {
    return doc.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5',
    );
  }
  return doc;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Falha ao ler arquivo'));
        return;
      }
      // result vem como "data:application/x-pkcs12;base64,XXXXX"
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
    reader.readAsDataURL(file);
  });
}
